"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthModule = void 0;
const msal_browser_1 = require("@azure/msal-browser");
const MSALConfiguration_1 = __importDefault(require("./MSALConfiguration"));
const EventEmitter_1 = require("../../utils/EventEmitter");
const Azure_1 = require("../../../models/Authentication/Azure");
class AuthModule {
    constructor(options) {
        const configuration = {
            auth: options,
            system: MSALConfiguration_1.default.system,
            cache: MSALConfiguration_1.default.cache,
        };
        this.publicPaths = (options === null || options === void 0 ? void 0 : options.publicPaths) || [];
        this.MSALClient = new msal_browser_1.PublicClientApplication(configuration);
        this.emitter = new EventEmitter_1.EventEmitter();
        this.account = null;
        this.signUpRedirectRequest = {
            authority: options.signupAuthority,
            scopes: options.scopes,
            redirectUri: `${window.location.protocol}//${window.location.host}/signin/authentication`,
        };
        this.signInRedirectRequest = {
            authority: options.signinAuthority,
            scopes: options.scopes,
            redirectUri: `${window.location.protocol}//${window.location.host}/signin/authentication`,
        };
        this.signInImpersonationRedirectRequest = {
            authority: options.impersonationAuthority,
            scopes: options.scopes,
            redirectUri: `${window.location.protocol}//${window.location.host}/signin/authentication`,
        };
        this.tokenRequest = {
            authority: options.signinAuthority,
            scopes: options.scopes,
        };
        this.changePasswordRedirectRequest = {
            authority: options.changePasswordAuthority,
            scopes: options.scopes,
            redirectUri: `${window.location.protocol}//${window.location.host}`,
        };
        this.signOutRedirctRequest = {
            authority: options.signupAuthority,
            postLogoutRedirectUri: `${window.location.protocol}//${window.location.host}/signin`,
        };
        this.silentRequest = {
            authority: options.signinAuthority,
            scopes: options.scopes,
            redirectUri: '/auth.html',
        };
        this.ssoRequest = {
            authority: options.signinAuthority,
            scopes: options.scopes,
            redirectUri: `${window.location.protocol}//${window.location.host}`,
        };
        this.editProfileRedirectRequest = {
            authority: options.editProfileAuthority,
            scopes: options.scopes,
            redirectUri: `${window.location.protocol}//${window.location.host}`,
        };
    }
    addEventCallback(callback) {
        return this.MSALClient.addEventCallback(callback);
    }
    removeEventCallback(callbackId) {
        return this.MSALClient.removeEventCallback(callbackId);
    }
    loadAuthModule() {
        return this.MSALClient.handleRedirectPromise()
            .then((resp) => {
            this.handleResponse(resp);
            return resp;
        })
            .catch((e) => {
            var _a;
            if (((_a = e === null || e === void 0 ? void 0 : e.errorMessage) === null || _a === void 0 ? void 0 : _a.indexOf('AADB2C90091')) >= 0) {
                // User came in from password or user data change form by cancelling the process.
                window.location.reload();
            }
            throw e;
        });
    }
    handleResponse(response) {
        if (window.parent !== window) {
            // We're in an iframe context, we can't redirect here.
            return;
        }
        const account = response ? response.account : this.getAccount();
        if (account) {
            return this.setAccount(account);
        }
        if (this.shouldRedirectToSignIn()) {
            return this.MSALClient.loginRedirect(this.signInRedirectRequest);
        }
    }
    shouldRedirectToSignIn() {
        const { pathname } = window.location;
        return (pathname !== '/' && !this.publicPaths.some((publicPath) => pathname.startsWith(publicPath)));
    }
    setAccount(newAccount) {
        this.account = newAccount;
        this.emitter.emit(Azure_1.AuthEventType.ACCOUNT_CHANGED, this.account);
    }
    getAccount() {
        const [currentAccount] = this.MSALClient.getAllAccounts();
        return currentAccount || null;
    }
    async signIn(options) {
        if (!options) {
            return this.MSALClient.loginRedirect(this.signInRedirectRequest);
        }
        const mergedOptions = { ...this.signInRedirectRequest, ...options };
        return this.MSALClient.loginRedirect(mergedOptions);
    }
    async signInImpersonation() {
        return this.MSALClient.loginRedirect(this.signInImpersonationRedirectRequest);
    }
    async signUp() {
        return this.MSALClient.loginRedirect(this.signUpRedirectRequest);
    }
    async changePassword() {
        return this.MSALClient.loginRedirect(this.changePasswordRedirectRequest);
    }
    async signOut(options) {
        if (!options) {
            return this.MSALClient.logoutRedirect(this.signOutRedirctRequest);
        }
        const mergedOptions = { ...this.signOutRedirctRequest, ...options };
        return this.MSALClient.logoutRedirect(mergedOptions);
    }
    async getToken() {
        var _a;
        if (!this.account) {
            this.account = this.getAccount();
        }
        this.tokenRequest.account = (_a = this.account) !== null && _a !== void 0 ? _a : undefined;
        return this.MSALClient.acquireTokenSilent(this.tokenRequest)
            .then((accessTokenResponse) => {
            return accessTokenResponse.accessToken;
        })
            .catch((error) => {
            console.log(error);
            if (error instanceof msal_browser_1.InteractionRequiredAuthError) {
                return this.MSALClient.acquireTokenRedirect(this.tokenRequest);
            }
            return Promise.reject(error);
        });
    }
    onAccountChanged(cb) {
        this.emitter.subscribe(Azure_1.AuthEventType.ACCOUNT_CHANGED, cb);
    }
    onUserSignedIn(cb) {
        this.emitter.subscribe(Azure_1.AuthEventType.USER_SIGNED_IN, cb);
    }
    onUserSignedOut(cb) {
        this.emitter.subscribe(Azure_1.AuthEventType.USER_SIGNED_OUT, cb);
    }
    async editProfile() {
        return this.MSALClient.loginRedirect(this.editProfileRedirectRequest);
    }
}
exports.AuthModule = AuthModule;
